import { useState, useRef, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import translations from "../i18n/translations";
import { motion } from "framer-motion";

export default function Contact() {
  const { lang } = useLanguage();
  const text = translations[lang].contact;

  const [form, setForm] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
    files: [],
    callback: false,
    phone: "",
    isCompany: false,
    companyName: "",
    companyPhone: "",
  });

  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const messageRef = useRef(null);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.style.height = "auto";
      messageRef.current.style.height =
        messageRef.current.scrollHeight + "px";
    }
  }, [form.message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setIsSuccess(false);

    try {
      const res = await fetch("https://helvetassist-backend-1.onrender.com/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (data.success) {
        setIsSuccess(true);

        setForm({
          name: "",
          email: "",
          service: "",
          message: "",
          files: [],
          callback: false,
          phone: "",
          isCompany: false,
          companyName: "",
          companyPhone: "",
        });
      } else {
        alert(text.error || "Error");
      }
    } catch (error) {
      console.log(error);
      alert(text.serverError || "Server error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* HEADER */}
      <section className="bg-gray-50 py-28 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto space-y-6"
        >
          <h2 className="text-5xl md:text-6xl font-extrabold text-red-600 tracking-tight">
            {text.title}
          </h2>

          <p className="text-3xl md:text-4xl font-light italic text-gray-700 leading-snug">
            “{text.subtitle}”
          </p>
        </motion.div>
      </section>

      {/* FORM */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="bg-white rounded-3xl soft-shadow p-10 md:p-14">
          <form onSubmit={handleSubmit} className="space-y-10">

            {/* NAME + EMAIL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  {text.name}
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="w-full mt-2 p-4 rounded-xl border border-slate-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  {text.email}
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full mt-2 p-4 rounded-xl border border-slate-300"
                />
              </div>
            </div>

            {/* SERVICE */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {text.serviceLabel}
              </label>
              <select
                name="service"
                value={form.service}
                onChange={handleChange}
                required
                className="w-full mt-1 p-4 rounded-xl border border-slate-300 bg-white"
              >
                <option value="" disabled hidden>
                  {text.servicePlaceholder}
                </option>
                <option value="Création et structuration d’entreprise (SA, Sàrl, RI)">{text.services.realEstate}</option>
                <option value="Secrétariat externalisé & assistance administrative ">{text.services.companies}</option>
                <option value="Support RH externalisé">{text.services.health}</option>
                <option value="Relocation & mobilité professionnelle">{text.services.tourism}</option>
                <option value="Conciergerie business & services premium">{text.services.expats}</option>
                <option value="Chauffeur & mobilité professionnelles">{text.services.family}</option>
                <option value="Assistance administrative personnelle">{text.services.pets}</option>
                <option value="Installation en Suisse – accompagnement des expatriésments">{text.services.events}</option>
                <option value="Services du quotidien & conciergerie">{text.services.other}</option>
                <option value="Immobilier & conciergerie résidentielle">{text.services.otherother}</option>
                <option value="Autre">{text.services.last}</option>
              </select>
            </div>

            {/* MESSAGE */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                {text.message}
              </label>
              <textarea
                ref={messageRef}
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                className="w-full mt-2 p-4 rounded-xl border border-slate-300 min-h-[180px]"
              />
            </div>

            {/* FILES */}
            <input type="file" multiple className="hidden" />

            {/* OPTIONS */}
            <div className="bg-slate-50 border rounded-2xl p-6 space-y-4">

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.callback}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      callback: e.target.checked,
                    }))
                  }
                />
                {text.callback}
              </label>

              {form.callback && (
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="tel"
                  placeholder={text.phone}
                  className="w-full p-3 border rounded-xl"
                />
              )}

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isCompany}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isCompany: e.target.checked,
                    }))
                  }
                />
                {text.company}
              </label>

              {form.isCompany && (
                <>
                  <input
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                    placeholder={text.companyName}
                    className="w-full p-3 border rounded-xl"
                  />

                  <input
                    name="companyPhone"
                    value={form.companyPhone}
                    onChange={handleChange}
                    placeholder={text.companyPhone}
                    className="w-full p-3 border rounded-xl"
                  />
                </>
              )}
            </div>

            {/* BUTTON */}
            <button
              type="submit"
              disabled={isSending}
              className="px-10 py-4 bg-red-600 text-white rounded-xl flex items-center justify-center gap-3 disabled:opacity-70"
            >
              {isSending && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {isSending ? text.sending : text.button}
            </button>

            {/* SUCCESS MESSAGE */}
            {isSuccess && (
              <div className="mt-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-center font-medium">
                ✅ {text.success}
              </div>
            )}

          </form>
        </div>
      </section>
    </>
  );
}